const cache = new Map()

// Don't derive the key from the options object: JSON.stringify per call was ~2.4x slower.
const getFormatter = (key, locale, buildOptions) => {
  let formatter = cache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, buildOptions())
    cache.set(key, formatter)
  }
  return formatter
}

const isEmpty = (value) => value === null || value === undefined || value === ''

const toNumber = (value) => {
  if (isEmpty(value)) return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isNaN(number) ? null : number
}

// es/es-ES set minimumGroupingDigits=2 by CLDR, so without 'always' 1000-9999 render
// ungrouped ("1836" instead of "1.836").
const INTEGER_OPTIONS = { maximumFractionDigits: 0, useGrouping: 'always' }
const PLAIN_OPTIONS = { maximumFractionDigits: 2, useGrouping: 'always' }

const currencyOptions = (currency) => ({ style: 'currency', currency, currencyDisplay: 'symbol', useGrouping: 'always' })

// When the currency is unknown, drop the symbol rather than the amount.
const formatPlain = (locale, number) => getFormatter(`plain|${locale}`, locale, () => PLAIN_OPTIONS).format(number)

export const formatInteger = (locale, amount) => {
  const number = toNumber(amount)
  if (number === null) return ''
  return getFormatter(`int|${locale}`, locale, () => INTEGER_OPTIONS).format(number)
}

export const formatDecimal = (locale, amount, decimals = 2) => {
  const number = toNumber(amount)
  if (number === null) return ''
  return getFormatter(`dec|${locale}|${decimals}`, locale, () => ({
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: 'always'
  })).format(number)
}

// The locale drives the suffix, not just the separator (es-CL: "3,7 M"; es-ES: "1,5 mil").
export const formatCompact = (locale, amount, decimals = 1) => {
  const number = toNumber(amount)
  if (number === null) return ''
  return getFormatter(`cmp|${locale}|${decimals}`, locale, () => ({
    notation: 'compact',
    maximumFractionDigits: decimals
  })).format(number)
}

export const formatCurrency = (locale, currency, amount) => {
  const number = toNumber(amount)
  if (number === null) return ''
  if (!currency) return formatPlain(locale, number)
  try {
    return getFormatter(`cur|${locale}|${currency}`, locale, () => currencyOptions(currency)).format(number)
  } catch (e) {
    return formatPlain(locale, number)
  }
}

// Defaults to 2, never 0: an unknown currency must not be mistaken for a 0-decimal one,
// or callers would round amounts to integers.
export const currencyDecimals = (locale, currency) => {
  if (!currency) return 2
  try {
    return getFormatter(`cur|${locale}|${currency}`, locale, () => currencyOptions(currency)).resolvedOptions().maximumFractionDigits
  } catch (e) {
    return 2
  }
}
